/**
 * Ziyaret Takip — ortak yardımcılar (API çağrısı, biçimleme, etiket sözlükleri).
 *
 * Backend `ApiResponse<T> { Success, Data, Message }` zarfı döndürür; `fetchVt`
 * zarfı açar ve hata mesajını fırlatır — her sekmenin ayrı ayrı zarf açması gerekmesin.
 */

import { authHeaders } from '../listPageStyles';

const BASE = '/api/visit-tracking';

/** Sorgu dizesi üretir; null/undefined/boş değerler ATLANIR (sunucuda "boş filtre" ≠ "filtre yok"). */
export function qs(params) {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    usp.append(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export async function fetchVt(path, params, options) {
  const res = await fetch(`${BASE}${path}${qs(params)}`, {
    headers: authHeaders(),
    ...options,
  });
  const payload = await res.json().catch(() => null);
  const ok = payload?.success ?? payload?.Success;
  if (!res.ok || ok === false) {
    throw new Error(payload?.message || payload?.Message || `İstek başarısız (${res.status}).`);
  }
  return payload?.data ?? payload?.Data ?? payload;
}

export async function postVt(path, body) {
  return fetchVt(path, null, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

/** Dışa aktarım — dosya indirir. Blob olarak alınır çünkü uç Bearer token ister. */
export async function downloadVt(path, params, fileName) {
  const res = await fetch(`${BASE}/export${path}${qs(params)}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Dışa aktarım başarısız (${res.status}).`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Biçimleme ────────────────────────────────────────────────────────────────

export function fmtTime(value, withSeconds = false) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  });
}

export function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Dakikayı "9 sa 28 dk" olarak yazar — çalışma süresi kolonlarının biçimi. */
export function fmtMinutes(min) {
  if (min === null || min === undefined) return '—';
  const total = Math.round(min);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h} sa ${String(m).padStart(2, '0')} dk` : `${m} dk`;
}

/** Saniyeyi ziyaret süresi olarak yazar: 5 dk altında saniye de gösterilir. */
export function fmtDuration(sec) {
  if (sec === null || sec === undefined) return '—';
  if (sec < 60) return `${sec} sn`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m < 5 && s > 0 ? `${m} dk ${s} sn` : `${m} dk`;
}

export function fmtMoney(value, digits = 2) {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function fmtNum(value) {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('tr-TR');
}

export function fmtPct(value) {
  if (value === null || value === undefined) return '—';
  return `%${Number(value).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`;
}

/** ISO tarih (yyyy-MM-dd) → gün başlangıcı/bitişi ISO damgası. */
export function dayStartIso(d) { return d ? new Date(`${d}T00:00:00`).toISOString() : ''; }
export function dayEndIso(d) { return d ? new Date(`${d}T23:59:59.999`).toISOString() : ''; }

export function todayIso() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── Etiket sözlükleri ────────────────────────────────────────────────────────

/** ZIYARET_TAKIP.md §4.2 — ziyaret sonucu. */
export const OUTCOME_LABELS = {
  success: 'Başarılı',
  no_sale: 'Satış Yok',
  not_found: 'Bulunamadı',
  closed: 'Kapalı',
  refused: 'Reddetti',
  cancelled: 'İptal',
};

export const OUTCOME_PILL = {
  success: 'g',
  no_sale: 'a',
  not_found: 'gr',
  closed: 'gr',
  refused: 'r',
  cancelled: 'r',
};

export const STATUS_LABELS = {
  OPEN: 'Sahada',
  CLOSED: 'Gün kapandı',
  AUTO_CLOSED: 'Gün kapatılmadı',
};

export const STATUS_PILL = {
  OPEN: 'g',
  CLOSED: 'gr',
  AUTO_CLOSED: 'a',
};

export const WORK_TYPE_LABELS = {
  P: 'P — Presales',
  K: 'K — Kamyon Satış',
  T: 'T — Tüccar',
};

/** Hareket grubu filtre çipleri (§5.3). GPS varsayılan KAPALI — akışı boğar. */
export const ACTIVITY_GROUPS = [
  { key: '', label: 'Tümü' },
  { key: 'visit', label: 'Ziyaret' },
  { key: 'document', label: 'Belge' },
  { key: 'collection', label: 'Tahsilat' },
  { key: 'survey', label: 'Anket / Bulunurluk' },
  { key: 'stock', label: 'Stok' },
  { key: 'day', label: 'Gün' },
];

export const PAYMENT_LABELS = {
  cash: 'Nakit',
  cheque: 'Çek',
  note: 'Senet',
  card: 'Kredi Kartı',
  dbs: 'DBS',
  transfer: 'Havale',
};

/**
 * Belge tipi → belge ekranı rotası. Belge no tıklanınca buraya gidilir.
 *
 * ⚠ Değerler AppRoutes.js'teki DETAY rotalarıyla birebir olmalıdır. #777.5: sipariş
 * `/order-headers/${id}` gösteriyordu ama `/order-headers` LİSTE sayfasıdır; siparişin detay
 * rotası `/orders/:id`'dir. Kayıtlı rotayla eşleşmediği için sipariş satırına tıklamak hiçbir
 * şey açmıyordu — fatura ve tahsilat açıldığı için sorun "bazı belgeler açılmıyor" gibi
 * görünüyordu. Yeni bir belge tipi eklerken rotayı AppRoutes'tan KOPYALAYARAK doğrula.
 */
export const DOCUMENT_ROUTES = {
  order: (id) => `/orders/${id}`,
  invoice: (id) => `/invoices/${id}`,
  dispatch: (id) => `/waybills/${id}`,
  waybill: (id) => `/waybills/${id}`,
  return: (id) => `/vendor-returns/${id}`,
};

/**
 * #777.4 Tahsilat hareketi → tahsilat kaydı rotası.
 *
 * Tahsilat hareketleri `documentType` TAŞIMAZ (belge değil, finans işlemidir) — bu yüzden
 * DOCUMENT_ROUTES'a düşmüyor ve akışta makbuz no'su link GİBİ görünüp tıklanamıyordu.
 * Kayıt `FinanceTransaction`'dır; ekran rotası ödeme yöntemine göre ayrışır
 * (bkz. finance/financeTypeMeta.js → GROUP.collection.routeBase + TYPES[].slug).
 */
const COLLECTION_SLUGS = {
  cash: 'cash',
  cheque: 'cheque',
  note: 'promissory-note',
  card: 'credit-card',
  dbs: 'dbs',
  transfer: 'bank-transfer',
};

/**
 * Ödeme tipi TAŞIMAYAN tahsilat hareketleri için son çare: yöntemi hareket KODUNDAN çöz
 * (`collection_cash` → `cash`). Mobil bugün `paymentType`'ı hep dolduruyor, ama logger yayına
 * girmeden önce yazılmış satırlarda alan boştur ve link yine çizilemezdi.
 *
 * <b>Tahmin YAPILMAZ:</b> kod da yöntemi söylemiyorsa (`collection_update`, `collection_delete`)
 * null döner ve satır tıklanamaz kalır — yanlış slug'a gitmek, çek kaydını nakit formunda açıp
 * kullanıcıya yanlış ekran göstermek demekti.
 */
function collectionSlugOf(a) {
  const byPayment = COLLECTION_SLUGS[a?.paymentType];
  if (byPayment) return byPayment;

  const code = String(a?.activityType || '');
  if (!code.startsWith('collection_')) return null;
  return COLLECTION_SLUGS[code.slice('collection_'.length)] || null;
}

/**
 * Bir hareketin gidilebilir kaydı varsa rotasını döner, yoksa null.
 *
 * #777.5 Link'in koşulu BELGE NO DEĞİL, gidilebilir bir kayıttır: mobilden gelen siparişlerde
 * belge no'nun üçü de (textId/printedNo/orderDescription) boş olabiliyor; eskiden link
 * `documentNo` dolu olmadıkça hiç render edilmediği için sipariş kaydına gidilemiyordu.
 */
export function activityTarget(a) {
  if (!a) return null;

  if (a.documentType && a.documentId) {
    const route = DOCUMENT_ROUTES[a.documentType];
    if (route) return route(a.documentId);
  }

  // Tahsilat: kayıt id'si mobilde collectionId ya da documentId alanından gelebilir.
  if (a.activityGroup === 'collection') {
    const id = a.collectionId || a.documentId;
    const slug = collectionSlugOf(a);
    if (id && slug) return `/finance/collections/${slug}/${id}`;
  }

  return null;
}

/** Link etiketi: belge/makbuz no varsa o, yoksa kayda gidildiğini anlatan kısa metin. */
export function activityTargetLabel(a) {
  if (a?.documentNo) return a.documentNo;
  return a?.activityGroup === 'collection' ? 'Tahsilat kaydını aç' : 'Belgeyi aç';
}

/** Saha İzleme sağ sütununun okuduğu, oturum satırlarında TOPLANABİLİR alanlar. */
const DAY_SUM_FIELDS = [
  'orderCount', 'orderAmount', 'orderSkuCount',
  'invoiceCount', 'invoiceAmount', 'invoiceSkuCount',
  'dispatchCount', 'dispatchAmount',
  'returnCount', 'returnAmount',
  'collectionAmount', 'collectionCash', 'collectionCheque', 'collectionNote',
  'collectionCard', 'collectionDbs',
  'plannedPointCount', 'visitedPointCount', 'visitCount',
  'successVisitCount', 'failVisitCount', 'skippedPointCount', 'offRouteVisitCount',
  'noSaleCount', 'surveyCount', 'availabilityCount',
  'totalWorkMin', 'totalVisitMin', 'totalTravelMin', 'totalKm', 'timeViolationCount',
];

/**
 * #777.6 Birden çok oturumu TEK özet nesnesine toplar.
 *
 * Saha İzleme'nin sağ sütunu `/days/{id}` detayından besleniyordu ve o uç yalnız Kokpit'ten bir
 * temsilci satırı seçilmişse çağrılıyordu — sekmeye doğrudan girildiğinde `dayDetail` null kalıp
 * sağdaki kartların HEPSİ boş ("—") görünüyordu. Seçim yoksa filtredeki oturumların toplamı
 * gösterilir.
 *
 * <b>Bilinmeyen 0 YAZILMAZ:</b> hiçbir satırda değer yoksa alan null kalır ve ekranda "—" çıkar —
 * aksi halde "hesaplanmadı" ile "gerçekten sıfır" ayırt edilemezdi (bkz. FillMissingCountsAsync).
 */
export function sumDayRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return null;

  const out = {};
  DAY_SUM_FIELDS.forEach((f) => {
    let sum = null;
    list.forEach((r) => {
      const v = r?.[f];
      if (v === null || v === undefined || Number.isNaN(Number(v))) return;
      sum = (sum ?? 0) + Number(v);
    });
    out[f] = sum;
  });

  // Gün başlangıcı/bitişi toplanamaz: en erken başlangıç ve en geç bitiş anlamlıdır.
  out.startedAt = list.map((r) => r?.startedAt).filter(Boolean).sort()[0] ?? null;
  const ends = list.map((r) => r?.endedAt).filter(Boolean).sort();
  out.endedAt = ends.length ? ends[ends.length - 1] : null;

  // Yüzde toplanamaz; toplamlardan yeniden hesaplanır.
  out.realizationPct = out.plannedPointCount
    ? (out.visitedPointCount ?? 0) / out.plannedPointCount * 100
    : null;

  out.sessionCount = list.length;
  return out;
}
